import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TRUEUP_FREQUENCIES, TIMEZONES, DURATION_UNITS } from "@/lib/constants";
import { Loader2, Settings } from "lucide-react";

export default function ProjectSettingsDialog({ open, onOpenChange, project, onSaved }) {
  const [freq, setFreq] = useState("weekly");
  const [customDays, setCustomDays] = useState(7);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [weekends, setWeekends] = useState(false);
  const [tz, setTz] = useState("UTC");
  const [unit, setUnit] = useState("days");
  const [holCal, setHolCal] = useState("us");
  const [customHols, setCustomHols] = useState("");
  const [program, setProgram] = useState("");
  const [progressStyle, setProgressStyle] = useState("partial");
  const [programs, setPrograms] = useState([]);
  const [calendars, setCalendars] = useState([{ key: "us", label: "United States (Federal)" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/holiday-calendars").then(({ data }) => setCalendars(data)).catch(() => {});
      api.get("/projects").then(({ data }) => setPrograms([...new Set(data.map((p) => p.program).filter(Boolean))].sort())).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (project) {
      setFreq(project.trueup_frequency || "weekly");
      setCustomDays(project.trueup_custom_days || 7);
      setStartDate(project.start_date || "");
      setEndDate(project.end_date || "");
      setWeekends(!!project.work_on_weekends);
      setTz(project.timezone || "UTC");
      setUnit(project.duration_unit || "days");
      setHolCal(project.holiday_calendar || "us");
      setCustomHols((project.custom_holidays || []).join(", "));
      setProgram(project.program || "");
      setProgressStyle(project.progress_style || "partial");
    }
  }, [project, open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, {
        name: project.name, description: project.description, industry: project.industry,
        trueup_frequency: freq, trueup_custom_days: freq === "custom" ? Number(customDays) : null,
        start_date: startDate || null, end_date: endDate || null,
        work_on_weekends: weekends, timezone: tz, duration_unit: unit,
        holiday_calendar: holCal,
        program: program.trim(),
        progress_style: progressStyle,
        custom_holidays: customHols.split(/[,\n]/).map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)),
      });
      toast.success("Settings saved");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="project-settings-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /> Project settings</DialogTitle>
          <DialogDescription>Set the schedule window, working calendar and how often your team trues up progress.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="s-program">Program</Label>
            <Input id="s-program" list="program-options" value={program} onChange={(e) => setProgram(e.target.value)} placeholder="e.g. Digital Transformation" className="mt-1.5" data-testid="settings-program-input" />
            <datalist id="program-options">{programs.map((p) => <option key={p} value={p} />)}</datalist>
            <p className="text-[11px] text-muted-foreground mt-1">Tag this project to a program to group it on the dashboard and portfolio.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="s-start">Start date</Label>
              <Input id="s-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5" data-testid="settings-start-date-input" />
            </div>
            <div>
              <Label htmlFor="s-end">Target end date</Label>
              <Input id="s-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5" data-testid="settings-end-date-input" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">Changing the start date reschedules the whole plan to begin then.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Holiday calendar</Label>
              <Select value={holCal} onValueChange={setHolCal}>
                <SelectTrigger className="mt-1.5" data-testid="settings-holiday-select"><SelectValue /></SelectTrigger>
                <SelectContent>{calendars.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="s-custom-hols">Company holidays</Label>
              <Input id="s-custom-hols" value={customHols} onChange={(e) => setCustomHols(e.target.value)} placeholder="2026-12-24, 2027-01-02" className="mt-1.5" data-testid="settings-custom-holidays-input" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">Holidays are skipped when scheduling and counting working days. Add extra company dates as comma-separated YYYY-MM-DD.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Time zone</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="mt-1.5" data-testid="settings-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="mt-1.5" data-testid="settings-duration-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DURATION_UNITS.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2.5 rounded-lg border border-border p-3 cursor-pointer hover:border-foreground/30 transition-all" data-testid="settings-weekends-toggle">
            <Checkbox checked={weekends} onCheckedChange={(v) => setWeekends(!!v)} data-testid="settings-weekends-checkbox" />
            <div>
              <p className="text-sm font-semibold">Count weekends as working days</p>
              <p className="text-[11px] text-muted-foreground">Affects duration, health and future scheduling.</p>
            </div>
          </label>
          <div>
            <Label>Progress style</Label>
            <Select value={progressStyle} onValueChange={setProgressStyle}>
              <SelectTrigger className="mt-1.5" data-testid="settings-progress-style-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="partial" data-testid="progress-style-partial">Partial progress (default) — average of each task's % complete</SelectItem>
                <SelectItem value="effort" data-testid="progress-style-effort">Effort-weighted — partial progress weighted by effort</SelectItem>
                <SelectItem value="done" data-testid="progress-style-done">Done tasks — completed ÷ all tasks</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">How % complete is reported on the dashboard, portfolio and status reports for this project.</p>
          </div>
          <div>
            <Label>True-up frequency</Label>
            <Select value={freq} onValueChange={setFreq}>
              <SelectTrigger className="mt-1.5" data-testid="settings-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TRUEUP_FREQUENCIES.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {freq === "custom" && (
            <div>
              <Label htmlFor="custom-days">Every N days</Label>
              <Input id="custom-days" type="number" min="1" value={customDays} onChange={(e) => setCustomDays(e.target.value)} className="mt-1.5 w-32" data-testid="settings-custom-days-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
